import React, { useState } from 'react'
import {
  Box,
  TextField,
  FormHelperText,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  MenuItem,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
import UploadIcon from '@mui/icons-material/Upload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  useGetDealerMysteryShopperCanSubmit,
  usePostDealerMysteryShopper,
} from '../../../api/promotions/dealer/dealer-mystery-shopper'
import BusinessNameAutocomplete from './BusinessNameAutocomplete'
import { useNavigate } from 'react-router-dom'

const MAX_FILE_SIZE = 5 // MB
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg']
const KEYWORD_OPTIONS = [0, 3, 4]

const StyledFieldBox = styled(Box)`
  && {
    max-width: 800px;
    justify-content: space-evenly;
    min-height: 80px;
    margin: auto;
    display: flex;
    flex-direction: column;
  }
`

const MysteryShopperForm = () => {
  const { register, handleSubmit, control, setValue, setError } = useForm({
    mode: 'onChange',
  })

  const navigate = useNavigate()
  const { mutateAsync, isLoading } = usePostDealerMysteryShopper()

  const { data } = useGetDealerMysteryShopperCanSubmit()

  const { enqueueSnackbar } = useSnackbar()

  const [openDialog, setopenDialog] = useState(false)

  const onSubmit = async (data) => {
    let formData = new FormData()

    for (let key in data) {
      // Add files
      if (['workshopPhoto'].includes(key)) {
        formData.append(key, data[key][0])
      } else {
        formData.append(key, data[key])
      }
    }

    try {
      const res = await mutateAsync(formData)

      setopenDialog(true)

      return res
    } catch (error) {
      // Display error on popup
      enqueueSnackbar(error?.response?.data?.message ?? 'Submit fail', {
        variant: 'error',
        autoHideDuration: 7000,
      })
    }
  }

  const handleClose = () => {
    // Close dialog
    setopenDialog(false)

    // Navigate back to home page
    navigate('/')

    // Scroll back to top
    window.scrollTo(0, 0)
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledFieldBox>
          <Controller
            name='businessName'
            control={control}
            defaultValue=''
            rules={{
              required: 'Business Name is required',
              maxLength: { value: 200, message: 'Exceed word count' },
            }}
            render={({ field: { onChange }, fieldState: { error } }) => (
              <BusinessNameAutocomplete
                onValueChange={({ name, address, state }) => {
                  onChange(name) // businessName field (validated string)
                  setValue('address', address)
                  setValue('state', state)
                }}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>

        <StyledFieldBox>
          <Controller
            name='phone'
            control={control}
            defaultValue=''
            rules={{
              required: 'Phone number is required',
              pattern: {
                value: /^(601)[0-46-9]-*[0-9]{7,8}$/,
                message: `Invalid phone number. Phone number should follow the format of 601xxxxxxxx. '+' sign is not required`,
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Phone Number'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                placeholder='60127123456'
              />
            )}
          />
        </StyledFieldBox>

        <StyledFieldBox>
          <Controller
            name='keywords'
            control={control}
            defaultValue=''
            rules={{
              required: 'Keywords is required',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                select
                label='Keywords'
                fullWidth
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                error={!!error}
                helperText={error ? error.message : null}
              >
                {KEYWORD_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </StyledFieldBox>

        <StyledFieldBox>
          <Controller
            name='mentionedVarta'
            control={control}
            defaultValue=''
            rules={{
              required: 'This field is required',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                select
                label='Did the owner or staff really mention VARTA?'
                fullWidth
                value={value}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              >
                <MenuItem value='Yes'>Yes</MenuItem>
                <MenuItem value='No'>No</MenuItem>
              </TextField>
            )}
          />
        </StyledFieldBox>

        <StyledFieldBox display='flex' flexDirection='column'>
          <Box mt={4}>
            <Controller
              name='workshopPhoto'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('workshopPhoto')
                // React Hook Form does not block field value being set on failing validation
                // This is a workaround by manually handling both the file size validation and value assignment
                const handleOnChange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2

                  const fileType = e?.target?.files[0]?.type

                  let errorMsg = null

                  // Validate file size
                  if (fileMb > MAX_FILE_SIZE) {
                    // Set error message
                    errorMsg = 'File size must be 5 MB or smaller'
                  } else if (!ALLOWED_FILE_TYPES.includes(fileType)) {
                    // Validate file type
                    errorMsg = 'Only PNG and JPG files are allowed'
                  }

                  // If there are errors
                  if (errorMsg) {
                    // Set error
                    setError(props.name, {
                      type: 'manual',
                      message: errorMsg,
                    })

                    // Reset field value
                    setValue(props.name, null)
                  } else {
                    // Set field value
                    props.onChange(e)
                  }
                }
                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload a photo of the workshop
                      <input
                        type='file'
                        accept={ALLOWED_FILE_TYPES}
                        hidden
                        {...props}
                        onChange={handleOnChange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                  </>
                )
              }}
            />
          </Box>
        </StyledFieldBox>
        <StyledFieldBox>
          <Box my={2}>
            <Typography variant='h4'>
              * Mystery Shopper submits the form on behalf of a participating dealer or workshop
              staff.
            </Typography>
            <Typography variant='h4'>
              * Mystery Shopper must submit the assessment form within 2–3 working days from the
              date of the visit.
            </Typography>
            <Typography variant='h4'>
              * All submissions must be completed through the designated portal.
            </Typography>
            <Typography variant='h4'>
              * The Mystery Shopper shall act in a fair, impartial, and professional manner when
              conducting the assessment.
            </Typography>
            <Typography variant='h4'>
              * The Mystery Shopper is responsible for obtaining accurate and complete information
              directly from the dealer or workshop during the visit.
            </Typography>
            <Typography variant='h4'>
              * The Mystery Shopper shall maintain the confidentiality of all information obtained
              during the assessment and shall not disclose such information to any unauthorized
              party.
            </Typography>
            <Typography variant='h4'>
              * HT Eco Battery Sdn Bhd reserves the right to amend, modify, or terminate the program
              and its Terms & Conditions at any time without prior notice.
            </Typography>
            <Typography variant='h4'>
              * By submitting the assessment form, the Mystery Shopper acknowledges and agrees to
              comply with these Terms & Conditions.
            </Typography>
          </Box>
          <Box>
            <Button
              fullWidth
              type='submit'
              size='large'
              variant='contained'
              disabled={isLoading || !data?.allow}
              endIcon={isLoading && <CircularProgress style={{ color: '#fff' }} size='1rem' />}
            >
              Submit
            </Button>
            <Typography variant='body2' color='error' mt={1}>
              {(() => {
                // If not allowed, return the message
                if (!data?.allow) return data?.message
                else if (isLoading) return 'Please wait while we process your submission.'
                else return null
              })()}
            </Typography>
          </Box>
        </StyledFieldBox>
      </form>
      <Dialog open={openDialog} onClose={handleClose}>
        <DialogContent dividers>
          <Box display='flex' justifyContent='flex-start' alignItems='center' pb={3}>
            <CheckCircleIcon style={{ color: '#4caf50' }} />
            <Typography variant={'h3'}>{'Form Submitted.'}</Typography>
          </Box>
          <Typography gutterBottom>Redirecting back to homepage...</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default MysteryShopperForm
